import { clsx } from '@alexis/helpers/clsx';
import { getCountryConfig } from '@alexis/helpers/country';
import { dateDisplayFormat } from '@alexis/helpers/date';
import { translateNumber } from '@alexis/helpers/translation';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import { getLocaleParam } from '@helpers/locale';
import { getNationalIdDoc, getPassportDoc } from '@helpers/traveller';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { TranslationsKey } from '@wegoTypes/translations';
import { Traveller } from '@wegoTypes/traveller';

import styles from './TravellerCard.module.scss';

interface TravellerCardProps {
  traveller: Traveller;
  onEdit?: () => void;
  onRemove?: () => void;
}
const TravellerCard = ({ traveller, onEdit, onRemove }: TravellerCardProps) => {
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);
  const localeParam = getLocaleParam();
  const [searchParams] = useSearchParams();

  const [passportDoc, nationalIdDoc] = useMemo(() => {
    return [
      getPassportDoc(traveller?.travellerDocuments),
      getNationalIdDoc(traveller?.travellerDocuments),
    ];
  }, [traveller]);

  return (
    <div className={styles.container}>
      <div className={styles.infoTitle}>
        <div className={styles.textPrimary}>{traveller.firstName + ' ' + traveller.lastName}</div>
        <div className={styles.textSecondary}>
          {traveller.travellerType === 'ADULT'
            ? translations.PassengerAdult
            : traveller.travellerType === 'CHILD'
            ? translations.PassengerChild
            : translations.PassengerInfant}
        </div>
      </div>
      <div className={clsx(styles.textSecondary, styles.infoDetails)}>
        <div>
          {traveller.gender && (
            <>
              <div>{traveller.gender === 'MALE' ? translations.male : translations.female}</div>
              <div className={styles.dot}></div>
            </>
          )}
          {traveller.dateOfBirth && (
            <>
              <div>
                {dateDisplayFormat(
                  new Date(traveller.dateOfBirth),
                  translations.short_months,
                  translations.short_weekdays,
                  locale === 'fa',
                  1,
                )}
              </div>
              <div className={styles.dot}></div>
            </>
          )}
          {traveller.nationality && (
            <div>
              {
                translations[
                  getCountryConfig(traveller.nationality)?.label as TranslationsKey
                ] as string
              }
            </div>
          )}
        </div>
        {passportDoc && (
          <div>
            <div>{translateNumber(passportDoc.documentNumber, locale === 'fa')}</div>
            <div className={styles.dot}></div>
            <div>
              {translations.expired || 'Expired'}{' '}
              {dateDisplayFormat(
                new Date(passportDoc.expiryDate || ''),
                translations.short_months,
                translations.short_weekdays,
                locale === 'fa',
                1,
              )}
            </div>
          </div>
        )}
        {nationalIdDoc && (
          <div>
            <div>{translateNumber(nationalIdDoc.documentNumber, locale === 'fa')}</div>
            <div className={styles.dot}></div>
            <div>
              {translations.expired || 'Expired'}{' '}
              {dateDisplayFormat(
                new Date(nationalIdDoc.expiryDate || ''),
                translations.short_months,
                translations.short_weekdays,
                locale === 'fa',
                1,
              )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        {traveller.primary ? (
          <Link
            to={{
              pathname: `${localeParam ? '/' + localeParam : ''}/preferences/profile/personal-info`,
              search: new URLSearchParams(searchParams.toString()).toString(),
            }}
          >
            {translations.edit}
          </Link>
        ) : (
          <div role='button' onClick={onEdit}>
            {translations.edit}
          </div>
        )}
        {!traveller.primary && (
          <div className={styles.removeBtn} role='button' onClick={onRemove}>
            {translations.remove}
          </div>
        )}
      </div>
    </div>
  );
};

export default TravellerCard;
